// Pure helpers for the Explorer → Products catalog page — filter-state
// defaults, option lists, loose-row field getters, and formatting utilities.
// Ported from identity-playground's products/channel/item-groups/categories
// page helpers.

import type {
  CategoriesFilterState,
  CategorySortBy,
  GroupingMethod,
  ItemGroupSortBy,
  ItemGroupsFilterState,
  ProductFilterState,
  SyncCapability,
} from '../types/products-catalog.types';

// ── Product filter state ───────────────────────────────────────────────────────

/** Default state for the Products-tab filter bar (score-desc sort). */
export const DEFAULT_FILTER_STATE: ProductFilterState = {
  search: '',
  grade: '',
  sourceStatus: '',
  hasCriticalIssues: false,
  vendors: [],
  sortBy: 'score_desc',
};

/** Connector categories that own a product catalog — used to scope the Products
 *  page connection dropdown to shopping-feed / commerce-platform connectors
 *  only (sent as the connection-list `category` filter). */
export const PRODUCT_CATALOG_CONNECTOR_CATEGORIES = 'SHOPPING_FEEDS,COMMERCE_PLATFORMS';

/** Grade chips shown in the products filter bar. */
export const GRADE_OPTIONS = ['excellent', 'good', 'fair', 'poor'] as const;
/** Source-status chips shown in the filter bar. */
export const SOURCE_STATUS_OPTIONS = ['approved', 'disapproved', 'pending'] as const;
/** Sort options (backend only supports these keys). */
export const SORT_OPTIONS = [
  { value: 'score_desc', label: 'Score ↓' },
  { value: 'score_asc', label: 'Score ↑' },
  { value: 'title', label: 'Title' },
  { value: 'issues', label: 'Issues' },
] as const;

/** Page-size choices for the products list. Capped at 100 — the backend's
 *  products-list pagination enforces `size ≤ 100` (PaginationParams `le=100`),
 *  so larger values 422 (previously surfaced as "Unable to load products"). */
export const PRODUCT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
/** Page-size choices for the item-groups / categories lists. */
export const GROUPING_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/** Builds the `filters` envelope the backend expects under the products-list body. */
export function buildProductsFilterEnvelope(state: ProductFilterState): Record<string, unknown> {
  const env: Record<string, unknown> = {};
  const search = state.search.trim();
  if (search) env.query = search;
  if (state.grade) env.grade = state.grade;
  if (state.sourceStatus) env.status = [state.sourceStatus.toUpperCase()];
  if (state.hasCriticalIssues) env.has_item_issues = true;
  if (state.vendors.length > 0) env.brand = state.vendors;
  return env;
}

// ── Loose-row field getters (tolerate camel + snake aliases) ────────────────────

/** Coerce a loose record value to a display string, skipping objects/symbols
 *  (which have no meaningful primitive representation). */
function asString(val: unknown): string {
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean' || typeof val === 'bigint') {
    return String(val);
  }
  return '';
}

/** Try multiple keys (plus their camel/snake variants) for a non-empty value. */
export function getProductField(product: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const val = asString(product[key]);
    if (val !== '') return val;
    const snake = key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
    const camel = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
    if (snake !== key) {
      const sv = asString(product[snake]);
      if (sv !== '') return sv;
    }
    if (camel !== key) {
      const cv = asString(product[camel]);
      if (cv !== '') return cv;
    }
  }
  return '—';
}

/** Formats a product's price from the many shapes connectors return. */
export function getProductPrice(product: Record<string, unknown>): string {
  if (product.priceAmount != null) {
    const amount = Number(product.priceAmount);
    if (!isNaN(amount) && amount > 0) {
      const currency = asString(product.priceCurrency).trim();
      return currency ? `${currency} ${amount.toFixed(2)}` : `$${amount.toFixed(2)}`;
    }
    return '—';
  }
  const price =
    product.price ??
    product.salePrice ??
    product.sale_price ??
    product.regularPrice ??
    product.regular_price;
  if (price == null) return '—';
  if (typeof price === 'object') {
    const p = price as Record<string, unknown>;
    const value = p.value ?? p.amount;
    const currency = p.currency ?? p.currencyCode ?? p.currency_code;
    if (value != null) return `${asString(currency)} ${asString(value)}`.trim();
  }
  return asString(price);
}

/** Humanises a product's source status / availability into a display label. */
export function getProductStatus(product: Record<string, unknown>): string {
  const status =
    product.sourceStatus ??
    product.source_status ??
    product.status ??
    product.availability ??
    product.state;
  if (status == null) return '—';
  return asString(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Semantic badge classes for a product's source/eligibility status (Merchant
 *  Center approval state), keyed by lowercased status with a neutral fallback.
 *  Shared by the products table Status column and the product card so both
 *  colour eligibility identically. */
export function getSourceStatusBadgeClass(status: string): string {
  const styles: Record<string, string> = {
    approved: 'border-success/20 bg-success-50 text-success',
    active: 'border-success/20 bg-success-50 text-success',
    disapproved: 'border-error/20 bg-error-50 text-error',
    pending: 'border-warning/20 bg-warning-50 text-warning',
    draft: 'border-warning/20 bg-warning-50 text-warning',
  };
  return styles[status.toLowerCase()] ?? 'border-border bg-muted text-muted-foreground';
}

/** Resolves the primary product image URL from various field/array shapes.
 *  Prefers the first `optimizedImages` entry (the enriched/CDN-served image),
 *  falling back to the raw image link/URL when none is present. */
export function getProductImage(product: Record<string, unknown>): string | null {
  const optimized: unknown = product.optimizedImages ?? product.optimized_images;
  if (Array.isArray(optimized) && optimized.length > 0) {
    const first: unknown = optimized[0];
    if (typeof first === 'string' && first.startsWith('http')) return first;
    if (typeof first === 'object' && first !== null) {
      const src = (first as Record<string, unknown>).src ?? (first as Record<string, unknown>).url;
      if (typeof src === 'string' && src.startsWith('http')) return src;
    }
  }

  const direct =
    product.imageLink ??
    product.image_link ??
    product.imageUrl ??
    product.image_url ??
    product.image ??
    product.src ??
    product.thumbnail;
  if (typeof direct === 'string' && direct.startsWith('http')) return direct;

  const images: unknown = product.images;
  if (Array.isArray(images) && images.length > 0) {
    const first: unknown = images[0];
    if (typeof first === 'string' && first.startsWith('http')) return first;
    if (typeof first === 'object' && first !== null) {
      const src = (first as Record<string, unknown>).src;
      if (typeof src === 'string' && src.startsWith('http')) return src;
    }
  }
  return null;
}

/** Derives a total-inventory count from variants or a GMC availability flag. */
export function getTotalInventory(product: Record<string, unknown>): number {
  const variants: unknown[] = Array.isArray(product.variants)
    ? (product.variants as unknown[])
    : [];
  if (variants.length === 0) {
    const availability = asString(product.availability).toLowerCase().trim();
    if (availability === 'in stock' || availability === 'preorder' || availability === 'in_stock')
      return 1;
    return 0;
  }
  return variants.reduce((sum: number, v) => {
    const qty = (v as Record<string, unknown>).inventory_quantity;
    return sum + (typeof qty === 'number' ? qty : parseInt(asString(qty), 10) || 0);
  }, 0);
}

// ── Capability + JSON helpers ───────────────────────────────────────────────────

/** Humanises a capability operation code (e.g. `max_batch` → `Max Batch`). */
export function capabilityLabel(op: string): string {
  return op.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** True when the given operation is present and supported in the capability set. */
export function isCapabilitySupported(capabilities: SyncCapability[], operation: string): boolean {
  return capabilities.some((c) => c.operation === operation && c.supported);
}

/** Pretty-prints any value as indented JSON (falls back to String on cycles). */
export function prettifyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Parses a JSON object from text; returns null on invalid or non-object input. */
export function parseProductJson(text: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

// ── Score grade ─────────────────────────────────────────────────────────────────

/** Maps a 0-100 quality score to a grade label + badge className. Uses semantic
 *  colour tokens (success/info/warning/error) so grades adapt to light/dark theme. */
export function getScoreGrade(score: number): {
  label: 'Excellent' | 'Good' | 'Fair' | 'Poor';
  className: string;
} {
  if (score >= 90)
    return {
      label: 'Excellent',
      className: 'bg-success-50 text-success border-success/20',
    };
  if (score >= 80) return { label: 'Good', className: 'bg-info-50 text-info border-info/20' };
  if (score >= 60)
    return { label: 'Fair', className: 'bg-warning-50 text-warning border-warning/20' };
  return { label: 'Poor', className: 'bg-error-50 text-error border-error/20' };
}

// ── Item-groups filter state ─────────────────────────────────────────────────────

/** Default state for the Item-Groups-tab filter bar. */
export const DEFAULT_ITEM_GROUPS_FILTER_STATE: ItemGroupsFilterState = {
  search: '',
  minProductCount: 0,
  minScore: 0,
  maxScore: 100,
  hasCriticalIssues: false,
  sortBy: 'count_desc',
  matchBy: 'ITEM_GROUP_ID',
};

/** Sort options for the item-groups list. */
export const ITEM_GROUP_SORT_OPTIONS: { value: ItemGroupSortBy; label: string }[] = [
  { value: 'count_desc', label: 'Most products' },
  { value: 'count_asc', label: 'Fewest products' },
  { value: 'score_desc', label: 'Highest score' },
  { value: 'score_asc', label: 'Lowest score' },
  { value: 'critical_desc', label: 'Most critical issues' },
  { value: 'critical_asc', label: 'Fewest critical issues' },
];

/** Minimum-product-count quick filters for the item-groups list. */
export const MIN_PRODUCT_COUNT_OPTIONS: number[] = [0, 2, 5, 10];

/** Grouping methods offered in the UI (MANUAL is intentionally excluded). */
export const SELECTABLE_GROUPING_METHODS: GroupingMethod[] = ['ITEM_GROUP_ID', 'MPN', 'OFFER_ID'];

// ── Categories filter state ──────────────────────────────────────────────────────

/** Default state for the Categories-tab filter bar. */
export const DEFAULT_CATEGORIES_FILTER_STATE: CategoriesFilterState = {
  search: '',
  minProductCount: 0,
  minScore: 0,
  maxScore: 100,
  hasCriticalIssues: false,
  sortBy: 'count_desc',
};

/** Sort options for the categories list. */
export const CATEGORY_SORT_OPTIONS: { value: CategorySortBy; label: string }[] = [
  { value: 'count_desc', label: 'Most products' },
  { value: 'count_asc', label: 'Fewest products' },
  { value: 'score_desc', label: 'Highest score' },
  { value: 'score_asc', label: 'Lowest score' },
  { value: 'name_asc', label: 'Name A → Z' },
  { value: 'name_desc', label: 'Name Z → A' },
  { value: 'critical_desc', label: 'Most critical issues' },
];
